import { DialogActions, FormControl, FormHelperText, Grid, makeStyles, MenuItem, Select, TextField, Typography, InputLabel } from "@material-ui/core";
import PropTypes from "prop-types";
import React from "react";
import { Controller, useForm } from "react-hook-form/dist/react-hook-form.ie11";
import { ITEM_COPY_FORM } from "../../data/StringConstants";
import CustomButton from "../CustomButton/CustomButton";
import { useToggle } from '../../hooks/useToggle';

const useStyles = makeStyles(theme => ({
    root: {
        "& .MuiFormControl-root": {
            margin: theme.spacing(4, 0),
        }
    }
}));

/**
 * A component which represents the form used when copy an Item.
 * @param {Object} data The data used to populate form controls
 * @param {{id: number, name: string}[]} data.folders An array of objects containing the name and Id of available folders to copy a given item to
 * @param {object} data.item The Item which is being copied
 * @param {number} data.targetFolder The ID of the Folder to copy the Item to
 */
export default function CopyItemForm({ onSubmit, onCancel, data }) {
    const classes = useStyles();

    const { folders, folderOfOrigin, item } = data;

    const [shouldDisable, setShouldDisable] = useToggle(false);

    const defaultValues = {
        targetFolder: item.PromotionContextId,
        folders,
        name: "Copy of " + item.Name
    };

    const { control, register, handleSubmit, errors } = useForm({ defaultValues });

    const beginSubmit = (data, _e) => {
        setShouldDisable(true)
        onSubmit(data)
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <Typography variant="body1" component={"p"}>
                    {`${ITEM_COPY_FORM.BODY}`} <em>{`${defaultValues.name}`}</em>
                </Typography>
                <form className={classes.root} onSubmit={handleSubmit(beginSubmit)}>
                    <FormControl fullWidth>
                        <TextField
                            inputRef={register({ required: true, minLength: 1, maxLength: 100 })}
                            label={ITEM_COPY_FORM.FORM_CONTROLS.TEXT_INPUTS.ITEM_NAME.LABEL}
                            name="name"
                            inputProps={{
                            'aria-label': ITEM_COPY_FORM.FORM_CONTROLS.TEXT_INPUTS.ITEM_NAME.ARIA
                            }}
                            required
                        />
                        <FormHelperText error>
                            {errors.name &&
                                errors.name.type === "maxLength" && (ITEM_COPY_FORM.FORM_CONTROLS.TEXT_INPUTS.ITEM_NAME.ERROR)}
                        </FormHelperText>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel shrink>
                            {ITEM_COPY_FORM.FORM_CONTROLS.SELECT_INPUTS.TARGET_FOLDER.LABEL}
                        </InputLabel>
                        <Controller
                            name="targetFolder"
                            control={control}
                            defaultValue={folderOfOrigin}
                            as={
                                <Select labelId="target-folder"
                                    inputProps={{
                                    "aria-label": ITEM_COPY_FORM.FORM_CONTROLS.SELECT_INPUTS.TARGET_FOLDER.ARIA
                                    }}
                                    
                                >
                                    {folders.map(option => (
                                        <MenuItem
                                            key={option.CorrelationId}
                                            value={option.CorrelationId}
                                        >
                                            {option.Name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            }
                        />
                    </FormControl>

                    <DialogActions>
                        <CustomButton 
                            onClick={onCancel} 
                            aria-label={ITEM_COPY_FORM.FORM_CONTROLS.BUTTONS.CANCEL.ARIA}
                        >
                            {ITEM_COPY_FORM.FORM_CONTROLS.BUTTONS.CANCEL.LABEL}
                        </CustomButton>
                        <CustomButton 
                            disabled={shouldDisable}
                            color="primary"
                            variant="contained" 
                            type="submit" 
                            aria-label={ITEM_COPY_FORM.FORM_CONTROLS.BUTTONS.CONFIRM.ARIA}
                        >
                            {ITEM_COPY_FORM.FORM_CONTROLS.BUTTONS.CONFIRM.LABEL}
                        </CustomButton>
                    </DialogActions>
                </form>
            </Grid>
        </Grid>
    );
}

CopyItemForm.propTypes = {
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    data: PropTypes.shape({
        folders: PropTypes.array,
        folderOfOrigin: PropTypes.string,
        name: PropTypes.string,
        targetFolder: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string
        })
    })
};